import React, {
	createContext,
	useContext,
	useEffect,
	useState,
	useRef,
} from "react";
import { User, Session, PostgrestError } from "@supabase/supabase-js";
import { supabase, clearSupabaseAuthStorage } from "@/lib/supabase";
import { Database } from "@/database.types";

export interface Profile {
	id: string;
	email: string;
	name: string;
	is_admin: boolean;
	user_type: "mentee" | "mentor" | "admin";
	role?: "viewer" | "editor" | null;
	created_at: string;
	updated_at: string;
	tnc_accepted?: boolean | null;
	tnc_version?: string | null;
	tnc_accepted_at?: string | null;
	tutorial_intro_completed?: boolean | null;
	tutorial_intro_dismissed?: boolean | null;
	tutorial_intro_completed_at?: string | null;
	reflection_goal?: string | null;
	reflection_goal_set_at?: string | null;
}

interface AuthContextType {
	user: User | null;
	profile: Profile | null;
	session: Session | null;
	loading: boolean;
	showInactivityWarning: boolean;
	warningCountdown: number;
	signIn: (email: string, password: string) => Promise<{ error: any }>;
	signUp: (
		email: string,
		password: string,
		name: string,
		userType: "mentee" | "mentor",
	) => Promise<{ error: any }>;
	signOut: () => Promise<void>;
	updateProfile: (
		updates: Partial<Profile>,
	) => Promise<{ error: Error | PostgrestError | null }>;
	continueSession: () => void;
	resetActivityTimer: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [user, setUser] = useState<User | null>(null);
	const [profile, setProfile] = useState<Profile | null>(null);
	const [session, setSession] = useState<Session | null>(null);
	const [loading, setLoading] = useState(true);

	// Loading state will be managed by the auth state recovery process below

	// Auto-logout functionality - 15 minutes total with 30 second warning
	const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds
	const WARNING_DELAY = 14.5 * 60 * 1000; // Warning at 14.5 minutes (30 seconds before logout)
	const [lastActivity, setLastActivity] = useState<number>(Date.now());

	// Use refs for timer IDs to prevent stale closures and multiple timers
	const logoutTimerRef = useRef<number | null>(null);
	const warningTimerRef = useRef<number | null>(null);
	const countdownIntervalRef = useRef<number | null>(null);

	// Use refs for auth state to prevent stale closures in timer callbacks
	const userRef = useRef<User | null>(null);
	const sessionRef = useRef<Session | null>(null);
	const isLoggingOutRef = useRef<boolean>(false);

	// Page Visibility API: Track timer state for pause/resume
	const warningTimerStartTimeRef = useRef<number | null>(null);
	const logoutTimerStartTimeRef = useRef<number | null>(null);
	const isTabVisibleRef = useRef<boolean>(true);
	const remainingWarningTimeRef = useRef<number | null>(null);
	const remainingLogoutTimeRef = useRef<number | null>(null);

	const [isLoggingOut, setIsLoggingOut] = useState(false);
	const [showInactivityWarning, setShowInactivityWarning] = useState(false);
	const [warningCountdown, setWarningCountdown] = useState(30); // 30 seconds in seconds

	// Keep refs in sync with state
	useEffect(() => {
		userRef.current = user;
	}, [user]);

	useEffect(() => {
		sessionRef.current = session;
	}, [session]);

	useEffect(() => {
		isLoggingOutRef.current = isLoggingOut;
	}, [isLoggingOut]);

	// Reset inactivity timer
	// Optional remainingTime parameter allows resuming from paused state
	const resetActivityTimer = (
		remainingWarningTime?: number,
		remainingLogoutTime?: number,
	) => {
		// Always clear existing timers first
		if (logoutTimerRef.current) {
			clearTimeout(logoutTimerRef.current);
			logoutTimerRef.current = null;
		}
		if (warningTimerRef.current) {
			clearTimeout(warningTimerRef.current);
			warningTimerRef.current = null;
		}
		if (countdownIntervalRef.current) {
			clearInterval(countdownIntervalRef.current);
			countdownIntervalRef.current = null;
		}

		// Hide warning and reset countdown to 0 when user becomes active (unless resuming)
		if (!remainingWarningTime) {
			setShowInactivityWarning(false);
			setWarningCountdown(0); // Reset to 0, not 30 seconds
		}

		// Use refs to check auth state (prevents stale closure issues)
		if (isLoggingOutRef.current || !userRef.current || !sessionRef.current) {
			return;
		}

		const now = Date.now();
		setLastActivity(now);

		// Calculate warning timer delay (use remaining time if resuming, otherwise full delay)
		const warningDelay = remainingWarningTime ?? WARNING_DELAY;
		warningTimerStartTimeRef.current = now;
		remainingWarningTimeRef.current = null;

		// Set warning timer
		warningTimerRef.current = window.setTimeout(() => {
			// Use refs to check auth state in callback (prevents race conditions)
			if (
				userRef.current &&
				sessionRef.current &&
				!isLoggingOutRef.current &&
				isTabVisibleRef.current
			) {
				setShowInactivityWarning(true);
				setWarningCountdown(30); // Start countdown at 30 seconds for new warning
			}
		}, warningDelay);

		// Calculate logout timer delay (use remaining time if resuming, otherwise full timeout)
		const logoutDelay = remainingLogoutTime ?? INACTIVITY_TIMEOUT;
		logoutTimerStartTimeRef.current = now;
		remainingLogoutTimeRef.current = null;

		// Set logout timer
		logoutTimerRef.current = window.setTimeout(() => {
			// Use refs to check auth state in callback (prevents race conditions)
			if (
				!isLoggingOutRef.current &&
				userRef.current &&
				sessionRef.current &&
				isTabVisibleRef.current
			) {
				signOut();
			}
		}, logoutDelay);
	};

	// Pause timers when tab becomes hidden (Page Visibility API)
	const pauseTimers = () => {
		if (!isTabVisibleRef.current) return; // Already paused
		isTabVisibleRef.current = false;

		const now = Date.now();

		// If warning is showing, calculate remaining time from countdown
		if (showInactivityWarning) {
			// Warning countdown is active - logout will happen in countdown seconds
			const countdownSeconds = warningCountdown;
			remainingLogoutTimeRef.current = countdownSeconds * 1000; // Convert to milliseconds

			// Clear the logout timer if it exists (it should have already fired to show warning)
			if (logoutTimerRef.current) {
				clearTimeout(logoutTimerRef.current);
				logoutTimerRef.current = null;
			}
			logoutTimerStartTimeRef.current = null;

			// Warning timer has already fired, no need to store it
			remainingWarningTimeRef.current = null;
			if (warningTimerRef.current) {
				clearTimeout(warningTimerRef.current);
				warningTimerRef.current = null;
			}
			warningTimerStartTimeRef.current = null;
		} else {
			// Warning not showing - calculate remaining time for both timers

			// Calculate remaining time for warning timer
			if (warningTimerRef.current && warningTimerStartTimeRef.current) {
				const elapsed = now - warningTimerStartTimeRef.current;
				const remaining = Math.max(0, WARNING_DELAY - elapsed);
				remainingWarningTimeRef.current = remaining;
				clearTimeout(warningTimerRef.current);
				warningTimerRef.current = null;
				warningTimerStartTimeRef.current = null;
			}

			// Calculate remaining time for logout timer
			if (logoutTimerRef.current && logoutTimerStartTimeRef.current) {
				const elapsed = now - logoutTimerStartTimeRef.current;
				const remaining = Math.max(0, INACTIVITY_TIMEOUT - elapsed);
				remainingLogoutTimeRef.current = remaining;
				clearTimeout(logoutTimerRef.current);
				logoutTimerRef.current = null;
				logoutTimerStartTimeRef.current = null;
			}
		}

		// Pause countdown interval if warning is showing
		if (countdownIntervalRef.current) {
			clearInterval(countdownIntervalRef.current);
			countdownIntervalRef.current = null;
		}
	};

	// Resume timers when tab becomes visible (Page Visibility API)
	const resumeTimers = () => {
		if (isTabVisibleRef.current) return; // Already visible
		isTabVisibleRef.current = true;

		// Only resume if user is authenticated
		if (!userRef.current || !sessionRef.current || isLoggingOutRef.current) {
			return;
		}

		// If warning was showing when paused, we need to handle it specially
		if (showInactivityWarning && remainingLogoutTimeRef.current !== null) {
			// Warning countdown was in progress - resume with remaining time
			const remainingSeconds = Math.ceil(
				(remainingLogoutTimeRef.current ?? 0) / 1000,
			);
			setWarningCountdown(Math.max(1, remainingSeconds)); // Ensure at least 1 second

			// Set logout timer with remaining time
			if (remainingLogoutTimeRef.current > 0) {
				logoutTimerRef.current = window.setTimeout(() => {
					if (
						!isLoggingOutRef.current &&
						userRef.current &&
						sessionRef.current &&
						isTabVisibleRef.current
					) {
						signOut();
					}
				}, remainingLogoutTimeRef.current);
				logoutTimerStartTimeRef.current = Date.now();
			}

			// Countdown interval will be restarted by the countdown useEffect
			remainingWarningTimeRef.current = null;
			remainingLogoutTimeRef.current = null;
		} else {
			// Normal resume - timers were running but not at warning stage
			resetActivityTimer(
				remainingWarningTimeRef.current ?? undefined,
				remainingLogoutTimeRef.current ?? undefined,
			);
		}
	};

	// Comprehensive timer cleanup
	const clearAllTimers = () => {
		if (logoutTimerRef.current) {
			clearTimeout(logoutTimerRef.current);
			logoutTimerRef.current = null;
		}
		if (warningTimerRef.current) {
			clearTimeout(warningTimerRef.current);
			warningTimerRef.current = null;
		}
		if (countdownIntervalRef.current) {
			clearInterval(countdownIntervalRef.current);
			countdownIntervalRef.current = null;
		}
		setShowInactivityWarning(false);
		setWarningCountdown(0); // Reset to 0 when clearing

		// Clear pause/resume state
		warningTimerStartTimeRef.current = null;
		logoutTimerStartTimeRef.current = null;
		remainingWarningTimeRef.current = null;
		remainingLogoutTimeRef.current = null;
	};

	// Continue session - resets timer for another 15 minutes
	const continueSession = () => {
		// Use refs to check auth state (prevents stale closure issues)
		if (!userRef.current || !sessionRef.current || isLoggingOutRef.current) {
			return;
		}
		setShowInactivityWarning(false);
		resetActivityTimer();
	};

	// Countdown timer effect
	// Fixed: Removed user and session from dependencies to prevent unnecessary restarts
	// Using refs inside the interval callback to check auth state
	useEffect(() => {
		// Clear any existing countdown interval
		if (countdownIntervalRef.current) {
			clearInterval(countdownIntervalRef.current);
			countdownIntervalRef.current = null;
		}

		// Only run countdown if warning is showing and tab is visible
		if (showInactivityWarning && !isLoggingOut && isTabVisibleRef.current) {
			countdownIntervalRef.current = window.setInterval(() => {
				// Use refs to check auth state (prevents stale closure issues)
				if (
					!isLoggingOutRef.current ||
					!userRef.current ||
					!sessionRef.current ||
					!isTabVisibleRef.current
				) {
					// Stop countdown if user logged out or tab is hidden
					if (countdownIntervalRef.current) {
						clearInterval(countdownIntervalRef.current);
						countdownIntervalRef.current = null;
					}
					if (!isTabVisibleRef.current) {
						// Don't clear warning if tab is just hidden - it will resume when visible
						return;
					}
					setShowInactivityWarning(false);
					setWarningCountdown(0);
					return;
				}

				setWarningCountdown((prev) => {
					if (prev <= 1) {
						// Auto logout when countdown reaches 0
						setShowInactivityWarning(false);
						setWarningCountdown(0);
						// Use refs to check auth state before signing out
						if (
							!isLoggingOutRef.current &&
							userRef.current &&
							sessionRef.current &&
							isTabVisibleRef.current
						) {
							signOut();
						}
						return 0;
					}
					return prev - 1;
				});
			}, 1000);
		}

		return () => {
			if (countdownIntervalRef.current) {
				clearInterval(countdownIntervalRef.current);
				countdownIntervalRef.current = null;
			}
		};
	}, [showInactivityWarning, isLoggingOut]); // Removed user and session from dependencies

	// Page Visibility API: Pause/resume timers when tab is hidden/visible
	useEffect(() => {
		if (!user || !session || isLoggingOut) {
			return;
		}

		const handleVisibilityChange = () => {
			if (document.hidden) {
				// Tab became hidden - pause timers
				pauseTimers();
			} else {
				// Tab became visible - resume timers
				resumeTimers();
			}
		};

		// Set initial visibility state
		isTabVisibleRef.current = !document.hidden;

		// Listen for visibility changes
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [user, session, isLoggingOut]);

	// Track user activity
	useEffect(() => {
		// If user is not authenticated or logging out, clear everything and return
		if (!user || !session || isLoggingOut) {
			clearAllTimers();
			return;
		}

		// List of events that indicate user activity
		// Removed "mousemove" and "scroll" for privacy and performance - only track meaningful interactions
		const activityEvents = ["mousedown", "keypress", "touchstart", "click"];

		// Debounce activity updates - timer resets after user stops interacting for 500ms
		// This is better than throttling as it waits for user to finish their interaction
		let activityDebounce: number | null = null;

		const handleActivity = () => {
			// Use refs to check auth state (prevents stale closure issues)
			if (!userRef.current || !sessionRef.current || isLoggingOutRef.current) {
				return;
			}

			// Clear existing debounce timer
			if (activityDebounce) {
				clearTimeout(activityDebounce);
				activityDebounce = null;
			}

			// Set new debounce timer - will reset inactivity timer after 500ms of no activity
			activityDebounce = window.setTimeout(() => {
				// Double-check auth state using refs before resetting timer
				if (userRef.current && sessionRef.current && !isLoggingOutRef.current) {
					resetActivityTimer();
				}
				activityDebounce = null;
			}, 500); // Debounce: wait 500ms after last activity before resetting timer
		};

		// Add event listeners
		activityEvents.forEach((event) => {
			document.addEventListener(event, handleActivity, true);
		});

		// Set initial timer
		resetActivityTimer();

		// Cleanup
		return () => {
			activityEvents.forEach((event) => {
				document.removeEventListener(event, handleActivity, true);
			});

			if (activityDebounce) {
				clearTimeout(activityDebounce);
			}

			// Clear all inactivity timers on cleanup
			clearAllTimers();
		};
	}, [user, session, isLoggingOut]); // Re-run when user, session, or logout status changes

	useEffect(() => {
		// Reasonable timeout to prevent infinite loading - allow time for auth recovery
		const loadingTimeout = setTimeout(() => {
			console.log("Auth timeout: Setting loading to false after 12 seconds");
			setLoading(false);
			setUser(null);
			setSession(null);
			setProfile(null);
		}, 12000); // Give 12 seconds for auth recovery (increased from 5s for better reliability on slow networks)

		// Get initial session\n    console.log('AuthContext: Starting session recovery...');
		supabase.auth
			.getSession()
			.then(({ data: { session } }) => {
				clearTimeout(loadingTimeout);
				setSession(session);
				setUser(session?.user ?? null);
				if (session?.user) {
					fetchProfile(session.user.id);
				} else {
					setLoading(false);
				}
			})
			.catch((error) => {
				clearTimeout(loadingTimeout);
				// If we get an invalid refresh token or similar, clear local auth storage and sign out
				try {
					const msg = String(error?.message || "");
					if (
						msg.includes("Invalid Refresh Token") ||
						msg.includes("Refresh Token Not Found") ||
						msg.includes("invalid_grant")
					) {
						clearSupabaseAuthStorage();
						// Best-effort revoke
						supabase.auth.signOut().catch(() => {});
					}
				} catch {}
				setLoading(false);
				setUser(null);
				setSession(null);
				setProfile(null);
			});

		// Listen for auth changes
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((event, session) => {
			// try {
			//   // if (event === "PASSWORD_RECOVERY") {
			//   //   // Use sessionStorage instead of localStorage for recovery flags (privacy improvement)
			//   //   const path = window.location.pathname;
			//   //   if (path !== "/login" && path !== "/mentor-login") {
			//   //     // Ensure we land on login where the modal is implemented
			//   //     window.location.replace("/login?type=recovery");
			//   //     return;
			//   //   }
			//   // }
			//   if (event === "USER_UPDATED") {
			//     // Clear recovery flag after password update
			//   }
			// } catch {}

			setSession(session);
			setUser(session?.user ?? null);
			if (session?.user) {
				fetchProfile(session.user.id);
			} else {
				// User logged out - immediately clear all timers and state
				setProfile(null);
				setLoading(false);

				// Clear all inactivity timers immediately when logged out
				clearAllTimers();
				setIsLoggingOut(false); // Reset logout flag when logged out
			}
		});

		return () => {
			subscription.unsubscribe();
			clearTimeout(loadingTimeout);
		};
	}, []);

	const fetchProfile = async (userId: string) => {
		// Set a timeout for the profile fetch - increased for better reliability
		const fetchTimeout = setTimeout(() => {
			setLoading(false);
			setProfile(null);
		}, 2000);

		// Additional safety timeout - increased for better reliability
		const safetyTimeout = setTimeout(() => {
			setLoading(false);
		}, 4000);

		try {
			const { data, error } = await supabase
				.from("profiles")
				.select("*")
				.eq("id", userId);

			clearTimeout(fetchTimeout);
			clearTimeout(safetyTimeout);

			if (error) {
				setProfile(null);
			} else {
				if (!data || data.length === 0) {
					// User is authenticated but has no profile - create one for them
					// This is now the standard behavior to keep the app working
					try {
						const { data: userData } = await supabase.auth.getUser();
						if (userData.user) {
							const { data: newProfile, error: createError } = await supabase
								.from("profiles")
								.insert({
									id: userData.user.id,
									email: userData.user.email || "",
									name:
										userData.user.user_metadata?.name ||
										userData.user.email ||
										"User",
									user_type: userData.user.user_metadata?.user_type || "mentee",
								})
								.select()
								.single();

							if (createError) {
								// Always create a temporary profile so the app works
								const tempProfile = {
									id: userData.user.id,
									email: userData.user.email || "",
									name:
										userData.user.user_metadata?.name ||
										userData.user.email ||
										"User",
									user_type: (userData.user.user_metadata?.user_type ||
										"mentee") as "mentee" | "mentor",
									created_at: new Date().toISOString(),
									updated_at: new Date().toISOString(),
									is_admin: false,
								};
								setProfile(tempProfile);
							} else {
								setProfile(newProfile as Profile);
							}
						} else {
							setProfile(null);
						}
					} catch (createErr) {
						setProfile(null);
					}
				} else {
					setProfile(data[0]);
				}
			}
		} catch (error) {
			clearTimeout(fetchTimeout);
			clearTimeout(safetyTimeout);
			setProfile(null);
		} finally {
			clearTimeout(fetchTimeout);
			clearTimeout(safetyTimeout);
			setLoading(false);
		}
	};

	const signIn = async (email: string, password: string) => {
		try {
			const { data, error } = await supabase.auth.signInWithPassword({
				email,
				password,
			});

			// Handle specific error cases
			if (error) {
				// Check for email not confirmed error
				if (
					error.message?.includes("Email not confirmed") ||
					error.message?.includes("email_not_confirmed") ||
					error.message?.includes("signup_disabled")
				) {
					return {
						error: {
							...error,
							message:
								"Please check your email and click the verification link before signing in. If you can't find the email, check your spam folder.",
						},
					};
				}

				// Check for invalid credentials with helpful message
				if (
					error.message?.includes("Invalid login credentials") ||
					error.message?.includes("invalid_grant")
				) {
					return {
						error: {
							...error,
							message:
								"Invalid email or password. If you just signed up, please verify your email first.",
						},
					};
				}
			}

			return { error };
		} catch (err) {
			return { error: err };
		}
	};

	const signUp = async (
		email: string,
		password: string,
		name: string,
		userType: "mentee" | "mentor",
	) => {
		// Include user metadata for the trigger to use
		const { data, error } = await supabase.auth.signUp({
			email,
			password,
			options: {
				data: {
					name: name,
					user_type: userType,
				},
			},
		});

		if (error) {
			return { error };
		}

		if (!data.user) {
			return { error: new Error("No user data returned from signup") };
		}

		// Profile will be automatically created by the database trigger
		// No manual profile creation needed here

		return { error: null };
	};

	const signOut = async () => {
		// Set logout flag to prevent timer resets
		setIsLoggingOut(true);

		// Immediately clear all timers and warnings
		clearAllTimers();

		// Clear local state immediately (optimistic update)
		setProfile(null);
		setUser(null);
		setSession(null);

		const { error } = await supabase.auth.signOut();
		if (error) {
			console.error("Error signing out", error);
		}

		// Always clear storage regardless of API call result
		clearSupabaseAuthStorage();

		// Reset logout flag immediately - don't wait
		setIsLoggingOut(false);

		// Ensure timers are cleared
		clearAllTimers();
	};

	const updateProfile = async (updates: Partial<Profile>) => {
		if (!user) return { error: new Error("No user") };

		const { error, data } = await supabase
			.from("profiles")
			.update(updates)
			.eq("id", user.id);

		if (!error) {
			setProfile((prev) => (prev ? { ...prev, ...updates } : null));
		}

		return { error };
	};

	const value = {
		user,
		profile,
		session,
		loading,
		showInactivityWarning,
		warningCountdown,
		signIn,
		signUp,
		signOut,
		updateProfile,
		continueSession,
		resetActivityTimer,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
